/**
 * Session Data Service (local-first)
 *
 * All session history lives in the browser's localStorage -- there is no
 * database. This module keeps the same API-shaped interface
 * ({ success, data }) the pages already use.
 */

const SESSIONS_KEY = 'pe_sessions';

const newId = () =>
  (window.crypto && window.crypto.randomUUID)
    ? window.crypto.randomUUID()
    : `id_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const readStore = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key)) || [];
  } catch (error) {
    console.error(`Error reading ${key} from localStorage:`, error);
    return [];
  }
};

const writeStore = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value));
};

/**
 * Create or update the locally stored record of a chat session.
 * Called by the chat page as the conversation progresses.
 */
export const saveSessionSnapshot = async ({ sessionId, formData = {}, messages = [], status }) => {
  if (!sessionId) return { success: false, message: 'sessionId is required' };

  const sessions = readStore(SESSIONS_KEY);
  const now = new Date().toISOString();
  const index = sessions.findIndex(s => s.session_id === sessionId);
  const existing = index !== -1 ? sessions[index] : null;

  const record = {
    id: existing?.id || newId(),
    session_id: sessionId,
    chief_complaint: formData?.ChiefC || existing?.chief_complaint || '',
    doctor_specialty: formData?.DocSpec || existing?.doctor_specialty || '',
    status: status || existing?.status || 'active',
    message_count: messages.length,
    formData: formData && Object.keys(formData).length ? formData : existing?.formData || {},
    messages,
    created_at: existing?.created_at || now,
    updated_at: now,
    last_message_at: now
  };

  if (existing) {
    sessions[index] = record;
  } else {
    sessions.push(record);
  }
  writeStore(SESSIONS_KEY, sessions);
  return { success: true, data: record };
};

export const getSessionHistory = async (filters = {}) => {
  let sessions = readStore(SESSIONS_KEY);

  if (filters.status) {
    sessions = sessions.filter(s => s.status === filters.status);
  }

  sessions.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const offset = filters.offset || 0;
  const limit = filters.limit || sessions.length;
  return { success: true, data: sessions.slice(offset, offset + limit) };
};

export const getSessionSummary = async () => {
  const sessions = readStore(SESSIONS_KEY);
  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

  return {
    success: true,
    data: {
      total_sessions: sessions.length,
      active_sessions: sessions.filter(s => s.status === 'active').length,
      completed_sessions: sessions.filter(s => s.status === 'completed').length,
      recent_sessions: sessions.filter(s => new Date(s.created_at).getTime() > weekAgo).length,
      last_activity: sessions.length
        ? sessions.map(s => s.updated_at).sort().slice(-1)[0]
        : null
    }
  };
};

export const updateSessionStatus = async (sessionId, status) => {
  const sessions = readStore(SESSIONS_KEY);
  const index = sessions.findIndex(s => s.session_id === sessionId);
  if (index === -1) {
    return { success: false, message: 'Session not found' };
  }
  sessions[index] = { ...sessions[index], status, updated_at: new Date().toISOString() };
  writeStore(SESSIONS_KEY, sessions);
  return { success: true, message: `Session status updated to ${status}` };
};

/**
 * Load a stored session so the user can pick the conversation back up.
 * Returns the saved form data and full message history.
 */
export const continueSession = async (sessionId) => {
  const session = readStore(SESSIONS_KEY).find(s => s.session_id === sessionId);
  if (!session) {
    return { success: false, message: 'Session not found' };
  }
  return {
    success: true,
    data: {
      formData: session.formData || {},
      messages: session.messages || []
    }
  };
};

const sessionStore = {
  saveSessionSnapshot,
  getSessionHistory,
  getSessionSummary,
  updateSessionStatus,
  continueSession
};

export default sessionStore;
